import React, { useState, useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Sector,
  SectorProps,
} from "recharts";
import "../../../styles/charts/AssetsCategoryPieChart.scss";
import CommonSelect from "../../../../../../core/common/commonSelect";

interface SourceData {
  source: string;
  total_leads: number;
  converted: number;
  lost: number;
  in_process: number;
  conversion_rate: number;
  children?: SourceData[];
}

interface PieDataPoint {
  name: string;
  value: number;
  type: string;
  color: string;
  hasChildren: boolean;
  originalData: SourceData;
}

interface LeadSourcesPieChartProps {
  data: any;
}

type Coordinate = { x: number; y: number };

type PieSectorData = {
  percent?: number;
  name?: string | number;
  midAngle?: number;
  middleRadius?: number;
  tooltipPosition?: Coordinate;
  value?: number;
  paddingAngle?: number;
  dataKey?: string;
  payload?: any;
};

type PieSectorDataItem = React.SVGProps<SVGPathElement> &
  Partial<SectorProps> &
  PieSectorData;

const LeadSourcesPieChart: React.FC<LeadSourcesPieChartProps> = ({ data }) => {
  const [selectedMetric, setSelectedMetric] = useState<string>("total_leads");
  const [selectedPipeline, setSelectedPipeline] = useState<string>("-1");
  const [activeIndex, setActiveIndex] = useState<number | any>(0);
  const [navigationStack, setNavigationStack] = useState<
    Array<{
      sourceName: string;
      data: SourceData[];
    }>
  >([]);

  const colors = {
    total_leads: [
      "#8b5cf6",
      "#a78bfa",
      "#c4b5fd",
      "#ddd6fe",
      "#ede9fe",
      "#f3f0ff",
      "#e5dbff",
      "#d1c7fe",
    ],
    converted: [
      "#10b981",
      "#34d399",
      "#6ee7b7",
      "#9decda",
      "#a7f3d0",
      "#c6f6d5",
      "#86efac",
      "#4ade80",
    ],
    in_process: [
      "#f59e0b",
      "#fbbf24",
      "#fcd34d",
      "#fde68a",
      "#fef3c7",
      "#fffbeb",
      "#fef3c7",
      "#fde68a",
    ],
    lost: [
      "#ef4444",
      "#f87171",
      "#fca5a5",
      "#fecaca",
      "#fee2e2",
      "#fef2f2",
      "#fecdd3",
      "#fda4af",
    ],
  };

  const metricDisplayNames = {
    total_leads: "Total",
    converted: "Converted",
    in_process: "In Process",
    lost: "Lost",
  };

  const currentFilteredData = useMemo(() => {
    if (selectedPipeline === "-1") {
      return data.total;
    }
    return data.by_pipeline?.[selectedPipeline] || [];
  }, [data, selectedPipeline]);

  const currentLevelData = useMemo(() => {
    if (navigationStack.length === 0) {
      return currentFilteredData;
    }
    return navigationStack[navigationStack.length - 1].data;
  }, [currentFilteredData, navigationStack]);

  const getNumericValue = (item: SourceData, metric: string): number => {
    switch (metric) {
      case "total_leads":
        return item.total_leads || 0;
      case "converted":
        return item.converted || 0;
      case "in_process":
        return item.in_process || 0;
      case "lost":
        return item.lost || 0;
      default:
        return 0;
    }
  };

  const pieData = useMemo((): PieDataPoint[] => {
    const colorArray = colors[selectedMetric as keyof typeof colors];
    return currentLevelData
      .map((item: any, index: any) => {
        const value = getNumericValue(item, selectedMetric);
        return {
          name: item.source,
          value: value,
          type: selectedMetric,
          color: colorArray[index % colorArray.length],
          hasChildren: !!(item.children && item.children.length > 0),
          originalData: item,
        };
      })
      .filter((item: any) => item.value > 0);
  }, [currentLevelData, selectedMetric]);

  const renderActiveShape = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  }: PieSectorDataItem) => {
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * (midAngle ?? 1));
    const cos = Math.cos(-RADIAN * (midAngle ?? 1));
    const sx = (cx ?? 0) + ((outerRadius ?? 0) + 10) * cos;
    const sy = (cy ?? 0) + ((outerRadius ?? 0) + 10) * sin;
    const mx = (cx ?? 0) + ((outerRadius ?? 0) + 30) * cos;
    const my = (cy ?? 0) + ((outerRadius ?? 0) + 20) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <text
          x={cx}
          y={cy}
          dy={8}
          textAnchor="middle"
          fill="#333"
          fontSize={10}
          fontWeight={400}>
          {payload?.name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={(outerRadius ?? 0) + 6}
          outerRadius={(outerRadius ?? 0) + 10}
          fill={fill}
        />
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          textAnchor={textAnchor}
          fill="#333"
          fontSize={11}
          fontWeight={400}>
          {
            metricDisplayNames[
              selectedMetric as keyof typeof metricDisplayNames
            ]
          }
          : {value}
        </text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#666"
          fontSize={11}>
          {`(${((percent ?? 1) * 100).toFixed(1)}%)`}
        </text>
      </g>
    );
  };

  const handlePieClick = (data: any, index: number) => {
    const clickedItem = pieData[index];
    if (clickedItem && clickedItem.hasChildren) {
      const sourceData = currentLevelData.find(
        (item: any) => item.source === clickedItem.name
      );
      if (sourceData && sourceData.children) {
        setNavigationStack((prev) => [
          ...prev,
          {
            sourceName: clickedItem.name,
            data: sourceData.children!,
          },
        ]);
      }
      setActiveIndex(0);
    }
  };

  const handleMouseEnter = (data: any, index: number) => {
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveIndex(0);
  };

  const handleNavigateBack = (index: number) => {
    if (index === 0) {
      setNavigationStack([]);
    } else {
      setNavigationStack((prev) => prev.slice(0, index));
    }
    setActiveIndex(0);
    setSelectedMetric("total_leads");
  };

  const viewByOptions = [
    { value: "total_leads", label: "Total" },
    { value: "converted", label: "Converted" },
    { value: "in_process", label: "In Process" },
    { value: "lost", label: "Lost" },
  ];

  const pipelineOptions =
    data?.pipelines?.map((pipeline: any) => ({
      value: pipeline?.id?.toString(),
      label: pipeline?.pipeline_name,
    })) || [];

  const renderLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-RADIAN * midAngle);
    const y = cy + radius * Math.sin(-RADIAN * midAngle);

    return (
      <text
        x={x}
        y={y}
        fill="black"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={10}
        fontWeight="bold">
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  return (
    <div className="lead-sources-pie-chart-container">
      {/* Header Controls */}
      <div className="metric-selector">
        <div className="d-flex gap-2 align-items-center">
          <div style={{ width: "200px" }}>
            <CommonSelect
              className="select small text-muted"
              options={[
                { value: "-1", label: "All Pipelines" },
                ...pipelineOptions,
              ]}
              defaultValue={
                selectedPipeline
                  ? pipelineOptions.find(
                      (pipeline: any) => pipeline.value === selectedPipeline
                    )
                  : undefined
              }
              onChange={(selected: any) => {
                setSelectedPipeline(selected.value);
                setNavigationStack([]);
                setActiveIndex(0);
              }}
              placeHolder="Filter By Pipeline"
            />
          </div>
          <label className="metric-label">View by</label>
          <div style={{ minWidth: "115px" }}>
            <CommonSelect
              className="select small text-muted"
              options={viewByOptions}
              defaultValue={
                selectedMetric
                  ? viewByOptions.find((vw: any) => vw.value === selectedMetric)
                  : undefined
              }
              onChange={(selected: any) => {
                setSelectedMetric(selected.value);
                setActiveIndex(0);
              }}
              placeHolder="View By"
            />
          </div>

          <button
            className="btn btn-outline-secondary btn-sm py-2 d-flex align-items-center"
            onClick={() => handleNavigateBack(0)}
            disabled={navigationStack.length === 0}>
            <i className="ti ti-refresh me-1"></i>
            Back to Root
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-wrapper">
        {pieData && pieData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={90}
                outerRadius={130}
                paddingAngle={3}
                dataKey="value"
                nameKey="name"
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                onClick={handlePieClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                label={renderLabel}
                labelLine={false}>
                {pieData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    stroke={entry.hasChildren ? "#ffffff" : "transparent"}
                    strokeWidth={entry.hasChildren ? 2 : 0}
                    style={{
                      cursor: entry.hasChildren ? "pointer" : "default",
                    }}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="no-data-message">
            <i className="ti ti-database-off "></i>
            <p>No data available!</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="chart-legend">
        <div className="legend-items d-flex flex-start gap-1">
          {pieData.map((item, index) => (
            <div
              key={index}
              className={`legend-item ${
                activeIndex === index ? "active" : ""
              }`}>
              <div
                className="legend-dot"
                style={{ backgroundColor: item.color }}></div>
              <span className="legend-text" title={item.name}>
                {item.name.length > 15
                  ? `${item.name.substring(0, 12)}...`
                  : item.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LeadSourcesPieChart;
